import { getStorage, setStorage } from "../../Storage/utils/storage";
import { TelemetryLevel, effectiveTelemetryLevel } from "./telemetryRule";

// Re-exported so importers that want the rule AND the storage keep one import,
// while those that want only the rule can reach for `telemetryRule` and skip
// the storage/logger/SDK chain this module pulls in.
export { effectiveTelemetryLevel };
export type { TelemetryLevel };

/**
 * How much Datadog telemetry this DEVICE collects (DHT-5279).
 *
 * - `full`, everything, the shipped default.
 * - `noReplay`, RUM events and errors still collected, Session Replay off.
 *   Replay is the expensive half: a production trace measured its deflate
 *   worker at 52% of all profiled CPU. This rung exists so support can drop
 *   that cost on a struggling machine and still keep the error reporting they
 *   need to debug it. WEB ONLY, there is no native replay package, so the
 *   settings UI does not offer it on native.
 * - `off`, no collection at all.
 *
 * Deliberately per DEVICE rather than per user: a user preference stored
 * server-side would follow someone to every machine, and the question this
 * answers is "is this laptop misbehaving", not "does this person want
 * telemetry".
 *
 * Read synchronously at boot by `initTelemetry`, which runs at module scope
 * before React mounts.
 *
 * Note this module does NOT keep the telemetry SDK off its own boot path, and
 * an earlier version of this comment wrongly claimed it did: `storage` imports
 * `logger`, which imports the telemetry facade's log adapter, which can reach
 * `expo-datadog`. That is survivable only because the adapter requires the SDK
 * lazily and the entry files start telemetry first anyway.
 * Do not add imports here on the assumption that the boot path is protected.
 */

/** Storage key. Shared by both platforms; `storage` is the MMKV/localStorage split. */
export const TELEMETRY_LEVEL_STORAGE_KEY = "datadogTelemetryLevel";

/**
 * Last answer LaunchDarkly gave for the kill switch, so the NEXT boot can honour
 * it before LD has loaded.
 *
 * Without this the switch cannot take effect until LaunchDarkly initialises,
 * and on web that is deferred until the tenant context resolves after login
 * (`FeatureFlagProvider.web.tsx` passes `deferInitialization`, and
 * `useLDContext` withholds a context until `tenant.id` and `tenant.displayName`
 * exist). A killed tenant would otherwise have its login and first clinic
 * screen fully recorded on every single load.
 */
export const TELEMETRY_KILL_SWITCH_STORAGE_KEY = "datadogTelemetryKillSwitch";

const TELEMETRY_LEVELS: readonly string[] = ["full", "noReplay", "off"];

const isTelemetryLevel = (value: unknown): value is TelemetryLevel =>
  typeof value === "string" && TELEMETRY_LEVELS.includes(value);

/**
 * The level this device is set to, defaulting to `full`.
 *
 * Every failure path returns `full`: a device whose storage is unreadable
 * (private browsing, cleared site data, a corrupt value) should behave exactly
 * as it does today rather than silently losing telemetry. Opting out is a
 * deliberate act, never an accident of a failed read.
 */
export const getTelemetryLevel = (): TelemetryLevel => {
  try {
    const stored = getStorage<unknown>(TELEMETRY_LEVEL_STORAGE_KEY);
    return isTelemetryLevel(stored) ? stored : "full";
  } catch {
    return "full";
  }
};

/**
 * Persists the level for this device.
 *
 * The guard only covers a synchronous throw, which is the web path. On native
 * `setStorage` is `mmkvStorageInstance.setItem(...).then(null)`, so a failed
 * write surfaces as an unhandled rejection rather than reaching this catch, and
 * the choice is silently absent after the next launch. Fixing that belongs in
 * `storage.native.ts`, not here.
 */
export const setTelemetryLevel = (level: TelemetryLevel): void => {
  try {
    setStorage(TELEMETRY_LEVEL_STORAGE_KEY, level);
  } catch {
    // Nothing useful to do, and nothing safe to log from the SDK's own boot path.
  }
};

/**
 * How long a cached kill is honoured for.
 *
 * The cache exists to cover the window before LaunchDarkly answers, and it can
 * only ever be refreshed by a RESOLVED answer. If the flag is later archived,
 * or stops being exposed to client-side SDKs, `useResolvedBooleanFlag` returns
 * `undefined` for ever and there is no way to tell that apart from "not loaded
 * yet". Without an expiry, every device that cached a kill during an incident
 * would stay revoked permanently, with nothing in the UI to say why and no
 * recovery short of clearing storage. Seven days bounds that.
 */
const CACHED_KILL_SWITCH_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type CachedKillSwitch = { on: boolean; at: number };

const isCachedKillSwitch = (value: unknown): value is CachedKillSwitch =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CachedKillSwitch).on === "boolean" &&
  typeof (value as CachedKillSwitch).at === "number";

/**
 * Whether the last LaunchDarkly answer we saw killed telemetry, if it is recent
 * enough to trust. Defaults false, including for anything unreadable: a device
 * should collect as it does today rather than go quiet by accident.
 */
export const getCachedKillSwitch = (): boolean => {
  try {
    const stored = getStorage<unknown>(TELEMETRY_KILL_SWITCH_STORAGE_KEY);
    if (!isCachedKillSwitch(stored)) return false;

    return stored.on && Date.now() - stored.at < CACHED_KILL_SWITCH_TTL_MS;
  } catch {
    return false;
  }
};

/**
 * Records a RESOLVED LaunchDarkly answer. Never call this with a fallback: the
 * whole point is that the cache holds something LaunchDarkly actually said, and
 * writing a pre-initialisation `false` would erase a real kill from last
 * session. See `useResolvedBooleanFlag`.
 */
export const setCachedKillSwitch = (killSwitchOn: boolean): void => {
  try {
    const cached: CachedKillSwitch = { on: killSwitchOn, at: Date.now() };
    setStorage(TELEMETRY_KILL_SWITCH_STORAGE_KEY, cached);
  } catch {
    // As above.
  }
};

/**
 * The level the SDK should be initialised with, honouring the cached kill
 * switch so a killed tenant is not recorded during the window before
 * LaunchDarkly loads.
 */
export const getBootTelemetryLevel = (): TelemetryLevel =>
  effectiveTelemetryLevel(getCachedKillSwitch(), getTelemetryLevel());
